import { CenterPoint } from '../../../client/store/loops/types';
import { TaskPriorityColors } from '../../../client/store/users/types';

export interface UserRoleAssignmentContract {
  id: string;
  role: Role;
  clusterId?: string;
  clusterName?: string;
}

export interface ScopedPermissions {
  org: Permission[];
  cluster: Record<string, Permission[]>;
}

export interface BaseUserContract {
  id: string;
  email: string;
  status: UserStatus;
}

export interface MinimalUserContract extends BaseUserContract {
  firstName?: string;
  lastName?: string;
  lastLogin: Date;
  invitationUrl?: string;
}

export interface UserContract extends MinimalUserContract {
  roleAssignments: UserRoleAssignmentContract[];
  permissions: ScopedPermissions;
  enabledFeatures: OrgFeature[];
  homeAddress?: HomeAddressContract;
  preferredLanguage: string;
  impersonatedBy: UserContract;
  salesOrganizationId: string;
  mapFilterDefaults?: string[];
  rangeInM: number;
  centerPoint: CenterPoint;
  startTime: string;
  endTime: string;
  averageVisitDurationInMinutes: number;
  taskPriorityColors?: TaskPriorityColors;
}

export interface HomeAddressContract {
  placeId: string;
  fullAddress: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

export enum UserStatus {
  ACTIVE = 'active',
  NEW = 'new',
  INVITED = 'invited',
  DEACTIVATED = 'deactivated',
}

export enum Role {
  SALES_REP = 'sales_rep',
  ADMIN = 'admin',
  CLUSTER_ADMIN = 'cluster_admin',
  SALES_ORG_ADMIN = 'sales_org_admin',
}

export const ROLE_LEVEL: Record<Role, number> = {
  [Role.SALES_REP]: 1,
  [Role.CLUSTER_ADMIN]: 2,
  [Role.SALES_ORG_ADMIN]: 3,
  [Role.ADMIN]: 0,
};

/**
 * Authorization permissions. Mirror of the API's `Permission` enum
 * (blindspot-api/src/auth/types/permission.ts) - keys, values and order must
 * stay identical. Permissions arrive from the API as `ScopedPermissions`:
 *   - org bucket     -> applies across every cluster in the organization
 *   - cluster bucket -> applies only within the assigned cluster(s)
 * `CLUSTER_<resource>_READ` gates cluster-wide listings; plain `<resource>_READ`
 * gates per-record / caller-context reads. A permission controls WHO may call an
 * endpoint - the row scope (own / cluster / org) is set by the handler, not the
 * permission name.
 */
export enum Permission {
  // --- Self ---
  SELF_USER_UPDATE = 'SELF_USER_UPDATE',

  // --- Loops (route planning) ---
  LOOPS_READ = 'LOOPS_READ',
  LOOPS_CREATE = 'LOOPS_CREATE',
  LOOPS_EDIT = 'LOOPS_EDIT',
  LOOPS_CONFIRM = 'LOOPS_CONFIRM',

  // --- Accounts ---
  ACCOUNTS_READ = 'ACCOUNTS_READ', // per-record reads: own-rep search, org-scoped lookup & detail
  CLUSTER_ACCOUNTS_READ = 'CLUSTER_ACCOUNTS_READ', // cluster-wide account listings
  ACCOUNTS_CREATE = 'ACCOUNTS_CREATE',
  ACCOUNTS_UPDATE = 'ACCOUNTS_UPDATE',

  // --- Segments ---
  SEGMENTS_READ = 'SEGMENTS_READ',
  SEGMENTS_UPDATE = 'SEGMENTS_UPDATE',

  // --- Campaigns ---
  CAMPAIGNS_READ = 'CAMPAIGNS_READ',
  CAMPAIGNS_CREATE = 'CAMPAIGNS_CREATE',
  CAMPAIGNS_UPDATE = 'CAMPAIGNS_UPDATE',
  CAMPAIGNS_DELETE = 'CAMPAIGNS_DELETE',

  // --- Activities ---
  ACTIVITIES_READ = 'ACTIVITIES_READ', // across the org (cross-cluster)
  CLUSTER_ACTIVITIES_READ = 'CLUSTER_ACTIVITIES_READ', // own cluster only
  ACTIVITIES_CREATE = 'ACTIVITIES_CREATE',

  // --- Tasks ---
  // TASKS_READ in the ORG bucket grants "see other people's tasks"
  // (API TasksController.canSeeOthersTasks); in the cluster bucket it is own-only.
  TASKS_READ = 'TASKS_READ',
  TASKS_CREATE = 'TASKS_CREATE',
  TASKS_UPDATE = 'TASKS_UPDATE',
  TASKS_DELETE = 'TASKS_DELETE',

  // --- Favourite routes ---
  FAVOURITE_ROUTES_READ = 'FAVOURITE_ROUTES_READ',
  FAVOURITE_ROUTES_WRITE = 'FAVOURITE_ROUTES_WRITE',

  // --- Clusters ---
  CLUSTER_READ = 'CLUSTER_READ', // a single cluster
  CLUSTERS_READ = 'CLUSTERS_READ', // list clusters in the org
  CLUSTERS_CREATE = 'CLUSTERS_CREATE',
  CLUSTERS_UPDATE = 'CLUSTERS_UPDATE',

  // --- Sales organizations ---
  SALES_ORGANIZATION_READ = 'SALES_ORGANIZATION_READ', // a single org
  SALES_ORGANIZATIONS_READ = 'SALES_ORGANIZATIONS_READ', // list orgs
  SALES_ORGANIZATIONS_CREATE = 'SALES_ORGANIZATIONS_CREATE',
  SALES_ORGANIZATIONS_UPDATE = 'SALES_ORGANIZATIONS_UPDATE',
  SALES_ORGANIZATION_ACTIVATE = 'SALES_ORGANIZATION_ACTIVATE',
  SALES_ORGANIZATION_DEACTIVATE = 'SALES_ORGANIZATION_DEACTIVATE',

  // --- Users & access ---
  CLUSTER_SALES_REPS_READ = 'CLUSTER_SALES_REPS_READ', // read the reps in a cluster
  USER_ACTIVATE = 'USER_ACTIVATE',
  USER_DEACTIVATE = 'USER_DEACTIVATE',
  USER_IMPERSONATE = 'USER_IMPERSONATE',

  // --- Invitations ---
  SALES_REPS_INVITE = 'SALES_REPS_INVITE',
  CLUSTER_ADMIN_INVITE = 'CLUSTER_ADMIN_INVITE',
  SALES_ORG_ADMIN_INVITE = 'SALES_ORG_ADMIN_INVITE',
  ADMIN_INVITE = 'ADMIN_INVITE',

  // --- Files ---
  FILES_DOWNLOAD = 'FILES_DOWNLOAD',

  // --- System ---
  MAINTENANCE_TOGGLE = 'MAINTENANCE_TOGGLE',
}

export enum OrgFeature {
  TASKS_ACTIVITIES = 'TASKS_ACTIVITIES',
  CAMPAIGNS = 'CAMPAIGNS',
}

export function getClusterId(user: UserContract): string | undefined {
  const assignment = user.roleAssignments?.find((ra) => ra.clusterId);
  return assignment?.clusterId;
}

export function hasPermission(
  user: UserContract,
  permission: Permission,
): boolean {
  if (user.permissions?.org?.includes(permission)) return true;
  const clusterMap = user.permissions?.cluster ?? {};
  for (const perms of Object.values(clusterMap)) {
    if (perms.includes(permission)) return true;
  }
  return false;
}

export function hasPermissionInCluster(
  user: UserContract,
  permission: Permission,
  clusterId: string,
): boolean {
  if (user.permissions?.org?.includes(permission)) return true;
  return user.permissions?.cluster?.[clusterId]?.includes(permission) ?? false;
}

export function hasOrgPermission(
  user: UserContract | undefined,
  permission: Permission,
): boolean {
  return user?.permissions?.org?.includes(permission) ?? false;
}

export function hasAnyPermission(
  user: UserContract,
  permissions: Permission[],
): boolean {
  return permissions.some((p) => hasPermission(user, p));
}

export function hasAllPermissions(
  user: UserContract,
  permissions: Permission[],
): boolean {
  return permissions.every((p) => hasPermission(user, p));
}

export const CLUSTER_WRITE_PERMISSIONS: Permission[] = [
  Permission.LOOPS_CREATE,
  Permission.LOOPS_EDIT,
  Permission.LOOPS_CONFIRM,
  Permission.ACCOUNTS_CREATE,
  Permission.ACCOUNTS_UPDATE,
  Permission.SEGMENTS_UPDATE,
  Permission.CAMPAIGNS_CREATE,
  Permission.CAMPAIGNS_UPDATE,
  Permission.CAMPAIGNS_DELETE,
  Permission.SALES_REPS_INVITE,
  Permission.USER_ACTIVATE,
  Permission.USER_DEACTIVATE,
  Permission.FILES_DOWNLOAD,
];

export function canWriteInCluster(
  user: UserContract,
  clusterId: string,
): boolean {
  return CLUSTER_WRITE_PERMISSIONS.some((p) =>
    hasPermissionInCluster(user, p, clusterId),
  );
}

export function canReadCampaigns(
  user: UserContract,
  clusterId: string | undefined,
): boolean {
  if (!clusterId) return false;
  return hasPermissionInCluster(user, Permission.CAMPAIGNS_READ, clusterId);
}

export function hasRole(
  user: UserContract,
  role: Role,
  only: boolean = false,
): boolean {
  if (!user?.roleAssignments?.length) return false;
  if (only) return user.roleAssignments.every((ra) => ra.role === role);
  return user.roleAssignments.some((ra) => ra.role === role);
}

export function getUserMinMaxRole(
  user: UserContract,
  clusterId: string,
  minMax: 'min' | 'max',
): number {
  const levels = user.roleAssignments
    .filter((ra) => !ra.clusterId || ra.clusterId === clusterId)
    .map((ra) => ROLE_LEVEL[ra.role]);
  return levels.length > 0 ? Math[minMax](...levels) : -1;
}
